/**
 * TaskIODetailsView
 *
 * Custom READ view for the IO Request & Campaign Setup type's `requestTypeDetails`.
 * IO needs more than the generic declarative renderer can express — a dealType branch
 * (Channel Partner vs Advertiser money fields), a combined Flight Dates range, and a
 * Note that doubles as the task description — so it's registered as the IO type's
 * `DetailsView` and dispatched by the generic RequestTypeDetailsView.
 *
 * `details` is the already-parsed requestTypeDetails object (the dispatcher parses it).
 */

import React from 'react';
import PropTypes from 'prop-types';
import Text from 'antd/lib/typography/Text';
import { DetailCell } from './DynamicRequestDetails';
import { fmtDate, fmtMoney, hasValue } from './fieldHelpers';
import MentionText from '../../shared/MentionText';

const TaskIODetailsView = ({ details, hideNote }) => {
  if (!details) return null;

  const isChannelPartner = details.dealType === 'CHANNEL_PARTNER';
  const flight =
    hasValue(details.flightStartDate) || hasValue(details.flightEndDate)
      ? `${fmtDate(details.flightStartDate) || '—'} → ${fmtDate(details.flightEndDate) || '—'}`
      : null;

  return (
    <div className="task-rtd">
      <Text className="task-rtd__title">CAMPAIGN SETUP DETAILS</Text>

      <div className="task-rtd__grid">
        <DetailCell label="Flight Dates" value={flight} />
        <DetailCell label="Number of Months" value={details.numberOfMonths} />

        {isChannelPartner ? (
          <>
            <DetailCell label="Channel Partner CPM" value={fmtMoney(details.partnerCpm)} />
            <DetailCell label="Channel Partner Budget" value={fmtMoney(details.partnerTotalBudget)} />
            <DetailCell label="Advertiser CPM" value={fmtMoney(details.advertiserCpm)} />
          </>
        ) : (
          <>
            <DetailCell label="CPM" value={fmtMoney(details.cpm)} />
            <DetailCell label="Total Budget" value={fmtMoney(details.totalBudget)} />
          </>
        )}

        <DetailCell label="Website" value={details.website} isLink full />
        <DetailCell label="Clickthrough URL" value={details.clickthroughUrl} isLink full />
      </div>

      {!hideNote && hasValue(details.note) && (
        <div className="task-rtd__note">
          <span className="task-rtd__label">Note</span>
          <div className="task-rtd__note-text">
            <MentionText text={details.note} />
          </div>
        </div>
      )}
    </div>
  );
};

TaskIODetailsView.propTypes = {
  details: PropTypes.object,
  // When true, omit the Note row — used where an editable Note/Description block renders it instead.
  hideNote: PropTypes.bool,
};

TaskIODetailsView.defaultProps = {
  details: null,
  hideNote: false,
};

export default TaskIODetailsView;
